/**
 * Shipping Disclaimer — checkout gate
 * -----------------------------------
 * Hardens the cart checkout flow so the customer cannot proceed until they
 * accept the shipping disclaimer.
 *
 * Bypass-prevention strategy:
 *  1. The button is rendered with `disabled` server-side, so JS-off users
 *     also cannot click it.
 *  2. Form submit is intercepted at CAPTURE phase — runs before any other
 *     theme/app handler (Dawn cart, Shopify analytics, etc.).
 *  3. Click handler on the checkout button uses capture too, blocking
 *     synthetic clicks dispatched by other scripts.
 *  4. Enter-key presses inside the cart form are gated.
 *  5. `pageshow` + `visibilitychange` re-evaluate state on bfcache restores.
 *  6. MutationObserver re-binds if the cart re-renders via the Section
 *     Rendering API.
 *  7. Listens for Dawn's `cart:refresh` / generic `cart:updated` events.
 *
 * Note: This is UX-level enforcement. The server (checkout) cannot enforce
 * a custom checkbox. For hard server-side enforcement, pair this with a
 * checkout UI extension or a Function. For most use cases this gate is
 * sufficient and prevents accidental bypass.
 */
(function () {
  'use strict';

  const SELECTORS = {
    container: '[data-shipping-disclaimer]',
    checkbox: '[data-shipping-disclaimer-checkbox]',
    error: '[data-shipping-disclaimer-error]',
    checkoutBtn: '.cart__checkout-button[data-disclaimer-required="true"]',
    cartForm: 'form[action$="/cart"], form#cart, form[action="/cart"]',
    additionalBtns: '.additional-checkout-buttons[data-disclaimer-gates-additional="true"]'
  };

  const STATE_ATTR = 'data-disclaimer-locked';

  function init(root) {
    const scope = root || document;
    const container = scope.querySelector(SELECTORS.container);
    const checkbox = scope.querySelector(SELECTORS.checkbox);
    const checkoutBtn = scope.querySelector(SELECTORS.checkoutBtn);

    if (!container || !checkbox || !checkoutBtn) return;

    // Avoid double-binding on re-render
    if (checkoutBtn.dataset.disclaimerBound === 'true') {
      syncButtonState(checkbox, checkoutBtn);
      return;
    }
    checkoutBtn.dataset.disclaimerBound = 'true';

    // Initial sync (handles bfcache where checkbox state persists)
    syncButtonState(checkbox, checkoutBtn);

    // Toggle state when checkbox changes
    checkbox.addEventListener('change', () => {
      syncButtonState(checkbox, checkoutBtn);
      if (checkbox.checked) {
        clearError(container);
      }
    });

    // Block direct click on checkout button (capture phase)
    checkoutBtn.addEventListener(
      'click',
      (event) => {
        if (!checkbox.checked) {
          event.preventDefault();
          event.stopImmediatePropagation();
          flagError(container, checkbox);
        }
      },
      true
    );

    // Block form submit (capture phase, runs before Dawn/app handlers)
    const form = checkoutBtn.form || document.querySelector(SELECTORS.cartForm);
    if (form && form.dataset.disclaimerBound !== 'true') {
      form.dataset.disclaimerBound = 'true';
      form.addEventListener(
        'submit',
        (event) => {
          // Only gate if THIS submit is heading to checkout.
          // The cart form is also used for "update" — name="update" submitter
          // shouldn't be blocked.
          const submitter = event.submitter;
          const isCheckoutSubmit =
            !submitter || submitter === checkoutBtn || submitter.name === 'checkout';
          if (isCheckoutSubmit && !checkbox.checked) {
            event.preventDefault();
            event.stopImmediatePropagation();
            flagError(container, checkbox);
          }
        },
        true
      );
    }

    // Block Enter-key submit from inputs inside the form
    if (form) {
      form.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' && !checkbox.checked) {
            const target = event.target;
            // Allow Enter inside textarea
            if (target && target.tagName === 'TEXTAREA') return;
            // If user is on the disclaimer checkbox itself, let native toggle happen
            if (target === checkbox) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            flagError(container, checkbox);
          }
        },
        true
      );
    }

    // Re-sync on bfcache restore
    window.addEventListener('pageshow', () => syncButtonState(checkbox, checkoutBtn));

    // Re-sync when the tab becomes visible (defensive)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        syncButtonState(checkbox, checkoutBtn);
      }
    });
  }

  function syncButtonState(checkbox, checkoutBtn) {
    const accepted = checkbox.checked;
    const additional = document.querySelector(SELECTORS.additionalBtns);

    if (accepted) {
      checkoutBtn.removeAttribute('disabled');
      checkoutBtn.removeAttribute('aria-disabled');
      checkoutBtn.setAttribute(STATE_ATTR, 'false');
      if (additional) additional.setAttribute(STATE_ATTR, 'false');
    } else {
      checkoutBtn.setAttribute('disabled', 'disabled');
      checkoutBtn.setAttribute('aria-disabled', 'true');
      checkoutBtn.setAttribute(STATE_ATTR, 'true');
      if (additional) additional.setAttribute(STATE_ATTR, 'true');
    }
  }

  function flagError(container, checkbox) {
    container.setAttribute('data-state', 'error');
    const errorEl = container.querySelector(SELECTORS.error);
    if (errorEl) errorEl.hidden = false;
    // Move focus to the checkbox so keyboard users land in the right place
    try { checkbox.focus({ preventScroll: false }); } catch (_) { checkbox.focus(); }
    // Strip animation class so it can re-trigger on next failed attempt
    setTimeout(() => container.removeAttribute('data-state'), 600);
    // Re-add for persistent error styling without animation
    setTimeout(() => container.setAttribute('data-state', 'error'), 620);
  }

  function clearError(container) {
    container.removeAttribute('data-state');
    const errorEl = container.querySelector(SELECTORS.error);
    if (errorEl) errorEl.hidden = true;
  }

  // ---- Lifecycle ---------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-init if the cart re-renders via the Section Rendering API
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.(SELECTORS.container) ||
          node.querySelector?.(SELECTORS.container)
        ) {
          init(node.parentNode || document);
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Hook into Dawn's cart events if present
  document.addEventListener('cart:refresh', () => init());
  document.addEventListener('cart:updated', () => init());
})();
